import { useState } from 'react';
import {
  BookOpen, Globe, Bot, MessageSquare, Map, Upload, Sparkles,
  ChevronDown, ChevronUp, CheckCircle, AlertCircle, Info, Star,
  ArrowRight, Users, Lightbulb, Shield,
} from 'lucide-react';

interface GuideSection {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color: string;
  steps: Array<{
    title: string;
    content: string;
    tip?: string;
    warning?: string;
  }>;
}

const sections: GuideSection[] = [
  {
    id: 'intro',
    icon: <Star size={20} />,
    title: 'Come funziona la piattaforma',
    subtitle: 'Capire il flusso di lavoro in 3 minuti',
    color: 'from-violet-500 to-purple-600',
    steps: [
      {
        title: '1. Crea l\'esperienza',
        content: 'Un\'esperienza è un percorso culturale: una mostra, un sito archeologico, un museo, un itinerario. Ogni esperienza ha un nome, una descrizione e una posizione geografica. L\'esperienza è il "contenitore" di tutto il resto.',
        tip: 'Pensa all\'esperienza come alla scheda di un luogo: cosa si visita, dove si trova, quanto dura.',
      },
      {
        title: '2. Assegna il Genius Loci',
        content: 'Il Genius Loci è la guida IA dell\'esperienza — un personaggio con una personalità specifica che parla con i visitatori tramite l\'app Glooci. Può essere un personaggio storico, mitologico o inventato, con una voce, uno stile e una conoscenza specifica del luogo.',
        tip: 'Un buon Genius Loci ha un nome evocativo legato al luogo (es. "Artemide" per un museo greco, "Lorenzo" per un\'esperienza rinascimentale).',
      },
      {
        title: '2.5 Configura APE (Assistente Prompt Editor)',
        content: 'Nella sezione APE puoi impostare prompt, regole e tono editoriale del tuo assistente IA. APE aiuta a mantenere coerenza tra contenuti, stile del Genius Loci e risposte generate.',
        tip: 'Definisci poche regole chiare (tono, lunghezza risposte, vincoli storici) prima di generare i testi: otterrai risultati più stabili.',
      },
      {
        title: '3. Configura gli Intent',
        content: 'Gli intent sono le "domande e risposte" del Genius Loci. Ogni intent corrisponde a una cosa che il visitatore potrebbe chiedere o fare durante la visita. Puoi aggiungere frasi di addestramento (come verrebbe formulata la domanda) e le risposte che il Genius Loci darà.',
      },
      {
        title: '4. Pubblica',
        content: 'Quando tutto è pronto, marca l\'esperienza come "Pubblicata". Apparirà immediatamente nell\'app Glooci per i visitatori.',
        tip: 'Puoi lavorare in bozza finché non sei soddisfatto. Solo le esperienze pubblicate sono visibili agli utenti.',
      },
    ],
  },
  {
    id: 'esperienze',
    icon: <Globe size={20} />,
    title: 'Creare e gestire le esperienze',
    subtitle: 'Tutto quello che devi sapere sulle esperienze culturali',
    color: 'from-blue-500 to-cyan-600',
    steps: [
      {
        title: 'Come creare una nuova esperienza',
        content: 'Vai su "Esperienze" nel menu laterale e clicca su "Nuova esperienza". Si aprirà un editor con tre sezioni: "Informazioni base", "Genius Loci" e "Copertina & Media". Compila almeno il nome dell\'esperienza, poi salva.',
        tip: 'Dopo il salvataggio puoi completare automaticamente i campi mancanti del Genius Loci dalla sezione dedicata, poi rifinire manualmente i contenuti.',
      },
      {
        title: 'L\'immagine di copertina',
        content: 'La copertina è l\'immagine mostrata nell\'app come anteprima dell\'esperienza. Vai nella tab "Copertina & Media" dell\'editor, clicca su "Carica nuova copertina" e seleziona un\'immagine dal tuo computer. L\'immagine viene salvata in modo sicuro su Glooci Platform Storage.',
        warning: 'Le immagini caricate precedentemente sul server legacy potrebbero non essere accessibili. In quel caso, carica una nuova immagine direttamente da questo pannello.',
      },
      {
        title: 'Latitudine e Longitudine',
        content: 'Le coordinate geografiche servono per mostrare l\'esperienza sulla mappa nell\'app. Puoi trovarle facilmente cercando il luogo su Google Maps: clicca con il tasto destro sul punto esatto e seleziona "Cosa c\'è qui?" per visualizzare le coordinate.',
        tip: 'La latitudine (LAT) è il numero che viene prima, la longitudine (LNG) è il secondo. Per l\'Italia, la latitudine è intorno a 41-47, la longitudine intorno a 6-18.',
      },
      {
        title: 'Pubblicare o mettere in bozza',
        content: 'Nella sezione "Informazioni base" troverai il flag "Esperienza pubblicata". Quando è attivo (spuntato), l\'esperienza è visibile agli utenti dell\'app. Puoi disattivarla in qualsiasi momento per nasconderla senza eliminarla.',
      },
    ],
  },
  {
    id: 'genius-loci',
    icon: <Bot size={20} />,
    title: 'Configurare il Genius Loci',
    subtitle: 'Come costruire la personalità della guida IA',
    color: 'from-amber-500 to-orange-600',
    steps: [
      {
        title: 'Cos\'è il Genius Loci',
        content: 'Il Genius Loci (dal latino "spirito del luogo") è l\'assistente virtuale che i visitatori incontrano nell\'app Glooci durante la visita. Parla, risponde alle domande, racconta storie. La sua personalità, voce e conoscenza sono configurabili da te.',
        tip: 'Il nome "Genius Loci" è un omaggio alla tradizione latina: ogni luogo ha il suo spirito protettore. La piattaforma Glooci porta questo concetto nell\'era digitale.',
      },
      {
        title: 'Scegliere la personalità (Ruota di Jung)',
        content: 'Nella sezione Genius Loci troverai la "Ruota degli Archetipi" di Carl Gustav Jung. Gli archetipi sono 12 modelli di personalità universali (l\'Eroe, il Saggio, il Custode, ecc.). Scegliere un archetipo definisce il carattere base del Genius Loci: come parla, come si comporta, che valori trasmette.',
        tip: 'Per un museo di arte classica potrebbe funzionare bene "Il Saggio". Per un percorso di avventura nel Parco Nazionale, "L\'Esploratore". Per una mostra su artisti locali, "Il Creatore".',
      },
      {
        title: 'La voce TTS (Text-to-Speech)',
        content: 'Puoi scegliere la voce con cui il Genius Loci parlerà all\'utente. Il servizio usa Google Cloud Text-to-Speech con voci italiane di alta qualità. Puoi scegliere tra voci maschili e femminili, con diverse tonalità e velocità. Usa i controlli di pitch e speed per personalizzarla.',
      },
      {
        title: 'La base di conoscenza',
        content: 'Puoi caricare documenti (PDF, testi) che il Genius Loci userà per rispondere alle domande specifiche sul luogo. Più documenti aggiungi, più sarà informato. Sono ideali le guide turistiche, le schede tecniche delle opere, i testi storici.',
        tip: 'Parti con 2-3 documenti chiari e ben strutturati. È meglio avere meno testi di qualità che molti testi confusi.',
      },
    ],
  },
  {
    id: 'intent',
    icon: <MessageSquare size={20} />,
    title: 'Gestire gli Intent',
    subtitle: 'Come configurare le risposte del Genius Loci',
    color: 'from-green-500 to-emerald-600',
    steps: [
      {
        title: 'Cos\'è un Intent',
        content: 'Un intent è una "voce" nel vocabolario del Genius Loci. Rappresenta qualcosa che il visitatore potrebbe chiedere o dire, e la risposta corrispondente. Ad esempio: l\'intent "Descrizione opera" potrebbe essere attivato quando l\'utente chiede "Parlami di questa statua" o "Cos\'è questo?" e risponde con la descrizione dell\'opera.',
      },
      {
        title: 'Frasi di addestramento',
        content: 'Le frasi di addestramento sono esempi di come un utente potrebbe esprimere la stessa richiesta. Più ne aggiungi, più preciso sarà il sistema nel capire cosa vuole il visitatore. Inserisci le frasi nella tab "Training Phrases" dell\'editor intent.',
        tip: 'Aggiungi almeno 5-10 frasi per ogni intent, usando varianti diverse (formale, informale, con o senza parole chiave).',
      },
      {
        title: 'Aggiungere un POI sulla mappa',
        content: 'Se l\'intent è legato a un punto fisico (es. "Descrizione del mosaico nella sala 3"), puoi aggiungere le coordinate geografiche nella tab "POI Mappa". Clicca sulla mappa per selezionare il punto esatto, oppure inserisci le coordinate manualmente. Questo permette all\'app di attivare l\'intent automaticamente quando il visitatore si avvicina al punto.',
        tip: 'I POI (Punti di Interesse) sono utilissimi per i percorsi outdoor o nei grandi musei con più sale.',
      },
      {
        title: 'Aggiungere media (immagini, audio)',
        content: 'Nella tab "Media" puoi caricare immagini o file audio da mostrare all\'utente quando il Genius Loci risponde con quell\'intent. Utile per mostrare foto di dettaglio di un\'opera, mappe, audio guide speciali.',
        warning: 'Carica immagini ottimizzate (preferibilmente JPG sotto 500KB) per garantire un\'esperienza fluida sull\'app mobile.',
      },
    ],
  },
  {
    id: 'ia',
    icon: <Sparkles size={20} />,
    title: 'Configurazione assistita con IA',
    subtitle: 'Risparmia tempo con l\'intelligenza artificiale',
    color: 'from-pink-500 to-rose-600',
    steps: [
      {
        title: 'Come funziona la configurazione assistita',
        content: 'La piattaforma integra un assistente IA per supportare la compilazione dei contenuti del Genius Loci e del prompt conversazionale. Le informazioni restano sempre modificabili manualmente prima della pubblicazione.',
        tip: 'Usa il risultato generato come base di lavoro, poi personalizzalo con la tua conoscenza del luogo. L\'IA accelera il primo impianto, ma la qualità finale dipende dalla revisione editoriale.',
      },
      {
        title: 'Generare la personalità del Genius Loci',
        content: 'Nella sezione Genius Loci puoi compilare i campi con il comando "Compila campi mancanti" e rifinire manualmente bio, voce, personalità e ruota di Jung. Il prompt di sistema viene collegato agli intent dell\'esperienza e può essere rigenerato dalla sezione Prompt o orchestrato in modo più avanzato tramite APE.',
      },
      {
        title: 'Quando usare APE',
        content: 'Usa APE quando vuoi controllare con precisione comportamento e qualità dell\'assistente: policy di risposta, stile narrativo, profondità storica, limiti su fonti e formato output.',
        tip: 'Per team editoriali, APE è il punto giusto per centralizzare linee guida e ridurre differenze tra esperienze diverse.',
      },
      {
        title: 'Attenzione: revisione umana sempre necessaria',
        content: 'L\'IA può commettere errori storici o proporre dettagli non accurati. Prima di pubblicare, verifica sempre i contenuti generati, specialmente date, nomi, riferimenti storici e informazioni specifiche sul luogo.',
        warning: 'Non pubblicare mai contenuti generati dall&apos;IA senza revisione accurata. La correttezza delle informazioni culturali è fondamentale.',
      },
    ],
  },
  {
    id: 'immagini',
    icon: <Upload size={20} />,
    title: 'Gestione immagini e media',
    subtitle: 'Come caricare e gestire i contenuti visivi',
    color: 'from-teal-500 to-cyan-600',
    steps: [
      {
        title: 'Dove vengono salvate le immagini',
        content: 'Le nuove immagini caricate da questa piattaforma vengono salvate su Glooci Platform, un servizio sicuro e affidabile. Le immagini precedenti risiedono sul server legacy di Glooci (platform.glooci.it). Le due fonti coesistono senza problemi.',
      },
      {
        title: 'Formati e dimensioni consigliati',
        content: 'Per le copertine delle esperienze: immagini JPEG o PNG, dimensione minima 1200×630 pixel, massimo 2MB. Per gli avatar del Genius Loci: immagini quadrate (1:1), almeno 400×400 pixel, formato JPEG o PNG. Per i media degli intent: JPEG/PNG per le immagini (max 1MB), MP3/WAV per gli audio (max 5MB).',
        tip: 'Usa immagini ad alta qualità ma compresse. Strumenti gratuiti come TinyPNG o Squoosh.app ti aiutano a ridurre il peso senza perdere qualità.',
      },
      {
        title: 'Immagini non visibili (server legacy)',
        content: 'Se le immagini delle esperienze precedenti non si caricano nella piattaforma, significa che sono salvate sul server legacy con un percorso non più accessibile. La soluzione è semplice: carica una nuova versione dell\'immagine tramite la tab "Copertina & Media" dell\'editor esperienze.',
        warning: 'Le immagini caricate sul server legacy non vengono eliminate quando aggiorni l\'immagine tramite questa piattaforma. Per un\'archiviazione ordinata, segnalale all\'amministratore di sistema.',
      },
    ],
  },
  {
    id: 'mappa',
    icon: <Map size={20} />,
    title: 'Mappe e geolocalizzazione',
    subtitle: 'Come usare le funzionalità geografiche',
    color: 'from-indigo-500 to-blue-600',
    steps: [
      {
        title: 'Trovare le coordinate GPS',
        content: 'Il modo più semplice per trovare le coordinate di un luogo: apri Google Maps, cerca il luogo, poi clicca con il tasto destro sul punto esatto. Vedrai apparire le coordinate nel formato "latitudine, longitudine". Copiale e incollale nei campi corrispondenti della piattaforma.',
        tip: 'Puoi anche usare what3words.com per trovare coordinate di punti molto precisi (utile per singole opere in un museo o punti di interesse specifici).',
      },
      {
        title: 'POI per gli intent (Punti di Interesse)',
        content: 'I POI sono coordinate geografiche associate a specifici intent. Quando un visitatore si avvicina a quel punto fisico con l\'app aperta, il Genius Loci si attiva automaticamente con quell\'intent. Perfetto per visite guidate outdoor o percorsi museali con più fermate.',
      },
      {
        title: 'Come usare il selettore mappa',
        content: 'Nell\'editor degli intent, nella tab "POI Mappa", trovi una mappa interattiva. Clicca sul punto esatto dove vuoi posizionare il POI. Le coordinate vengono compilate automaticamente. Puoi anche modificarle manualmente nei campi latitudine/longitudine.',
      },
    ],
  },
  {
    id: 'faq',
    icon: <Info size={20} />,
    title: 'Domande frequenti',
    subtitle: 'Soluzioni ai problemi più comuni',
    color: 'from-gray-500 to-slate-600',
    steps: [
      {
        title: 'Non riesco ad accedere alla piattaforma',
        content: 'La piattaforma usa due sistemi di autenticazione: Glooci Platform (per l\'accesso al pannello) e il sistema legacy Glooci (per i dati). Assicurati di avere credenziali valide. Se il problema persiste, contatta l\'amministratore del sistema.',
      },
      {
        title: 'Le modifiche non si salvano',
        content: 'Verifica che il campo "nome" dell\'esperienza sia compilato (è obbligatorio). Controlla la connessione internet. Se l\'errore persiste, prova ad aggiornare la pagina e ripetere l\'operazione. I dati non salvati andranno persi se esci dalla pagina.',
      },
      {
        title: 'L\'app Glooci non mostra le modifiche',
        content: 'Le modifiche sono immediate per i dati (testi, configurazioni). Le immagini possono richiedere qualche minuto per essere visibili sull\'app mobile a causa della cache. Se l\'esperienza non appare, verifica che sia stata marcata come "Pubblicata".',
      },
      {
        title: 'Come posso testare il Genius Loci prima di pubblicare',
        content: 'Puoi testare il Genius Loci direttamente sull\'app Glooci accedendo con un account di test. Imposta l\'esperienza come "non pubblicata" finché sei in fase di test, poi pubblicala quando sei soddisfatto del risultato.',
      },
      {
        title: 'Posso eliminare un\'esperienza?',
        content: 'Al momento la piattaforma supporta la disattivazione (mettere in bozza) delle esperienze. Per l\'eliminazione definitiva, contatta l\'amministratore tecnico. Prima di eliminare, considera che tutti gli intent e i dati associati andrebbero persi.',
        warning: 'L\'eliminazione di un\'esperienza è irreversibile. Valuta sempre di mettere in bozza invece di eliminare.',
      },
    ],
  },
];

const DocSection = ({ section }: { section: GuideSection }) => {
  const [open, setOpen] = useState(false);
  const [openStep, setOpenStep] = useState<number | null>(null);

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        className="w-full text-left p-5 flex items-center gap-4 hover:bg-gray-50 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center text-white shrink-0`}>
          {section.icon}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-text-primary">{section.title}</h3>
          <p className="text-sm text-text-secondary">{section.subtitle}</p>
        </div>
        <div className="shrink-0 text-text-secondary">
          {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {open && (
        <div className="border-t border-border">
          {section.steps.map((step, idx) => (
            <div key={idx} className="border-b border-border/50 last:border-0">
              <button
                type="button"
                className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-gray-50 transition-colors"
                onClick={() => setOpenStep(openStep === idx ? null : idx)}
              >
                <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-text-primary text-sm">{step.title}</p>
                </div>
                <div className="shrink-0 text-text-secondary mt-0.5">
                  {openStep === idx ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </button>
              {openStep === idx && (
                <div className="px-5 pb-5 pl-14 space-y-3">
                  <p className="text-sm text-text-secondary leading-relaxed">{step.content}</p>
                  {step.tip && (
                    <div className="flex gap-2.5 rounded-lg bg-primary/5 border border-primary/20 p-3">
                      <Lightbulb size={16} className="text-primary shrink-0 mt-0.5" />
                      <p className="text-xs text-primary leading-relaxed"><span className="font-semibold">Suggerimento:</span> {step.tip}</p>
                    </div>
                  )}
                  {step.warning && (
                    <div className="flex gap-2.5 rounded-lg bg-amber-50 border border-amber-200 p-3">
                      <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700 leading-relaxed"><span className="font-semibold">Attenzione:</span> {step.warning}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Documentazione = () => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? sections : sections.slice(0, 4);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Guida alla piattaforma</h1>
          <p className="text-text-secondary mt-1">Tutto quello che serve per gestire esperienze culturali con Glooci</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Users size={16} />
          <span>Per operatori culturali</span>
        </div>
      </div>

      {/* Quick start banner */}
      <div className="rounded-2xl bg-gradient-to-r from-primary via-accent to-tertiary p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
              <BookOpen size={20} />
              Nuovo su Glooci? Inizia da qui
            </h2>
            <p className="text-white/80 text-sm leading-relaxed">
              Glooci Platform è il CMS per creare esperienze culturali interattive guidate da intelligenza artificiale.
              In pochi passi puoi configurare la tua prima esperienza con una guida IA personalizzata.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 shrink-0">
            {[
              { n: '1', label: 'Crea esperienza', icon: <Globe size={18} /> },
              { n: '2', label: 'Configura Genius Loci', icon: <Bot size={18} /> },
              { n: '3', label: 'Pubblica', icon: <CheckCircle size={18} /> },
            ].map((s) => (
              <div key={s.n} className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center flex flex-col items-center gap-1.5">
                {s.icon}
                <p className="text-xs font-medium leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key concepts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            icon: <Globe size={22} className="text-blue-500" />,
            title: 'Esperienza',
            desc: 'Un percorso culturale: museo, sito, itinerario. È il punto di accesso per i visitatori.',
            bg: 'bg-blue-50 border-blue-100',
          },
          {
            icon: <Bot size={22} className="text-amber-500" />,
            title: 'Genius Loci',
            desc: 'La guida IA del luogo. Parla, racconta, risponde. Ha una personalità e una voce unica.',
            bg: 'bg-amber-50 border-amber-100',
          },
          {
            icon: <MessageSquare size={22} className="text-green-500" />,
            title: 'Intent',
            desc: 'Una coppia "domanda-risposta". Definisce cosa sa fare il Genius Loci e cosa risponde.',
            bg: 'bg-green-50 border-green-100',
          },
          {
            icon: <Sparkles size={22} className="text-fuchsia-500" />,
            title: 'APE',
            desc: 'Assistente Prompt Editor: definisce regole, tono e struttura delle risposte IA in modo coerente.',
            bg: 'bg-fuchsia-50 border-fuchsia-100',
          },
        ].map((c) => (
          <div key={c.title} className={`rounded-xl border p-4 ${c.bg}`}>
            <div className="flex items-center gap-3 mb-2">
              {c.icon}
              <h3 className="font-semibold text-text-primary">{c.title}</h3>
            </div>
            <p className="text-sm text-text-secondary">{c.desc}</p>
          </div>
        ))}
      </div>

      {/* Flusso visivo */}
      <div className="card p-5">
        <h3 className="font-semibold text-text-primary mb-4">Il flusso di lavoro consigliato</h3>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 flex-wrap">
          {[
            { icon: <Globe size={18} />, label: 'Crea l\'esperienza', color: 'bg-blue-100 text-blue-700' },
            { icon: <ArrowRight size={16} />, label: '', color: 'text-text-secondary' },
            { icon: <Upload size={18} />, label: 'Aggiungi la copertina', color: 'bg-teal-100 text-teal-700' },
            { icon: <ArrowRight size={16} />, label: '', color: 'text-text-secondary' },
            { icon: <Bot size={18} />, label: 'Configura il Genius Loci', color: 'bg-amber-100 text-amber-700' },
            { icon: <ArrowRight size={16} />, label: '', color: 'text-text-secondary' },
            { icon: <Sparkles size={18} />, label: 'Definisci APE', color: 'bg-fuchsia-100 text-fuchsia-700' },
            { icon: <ArrowRight size={16} />, label: '', color: 'text-text-secondary' },
            { icon: <MessageSquare size={18} />, label: 'Crea gli intent', color: 'bg-green-100 text-green-700' },
            { icon: <ArrowRight size={16} />, label: '', color: 'text-text-secondary' },
            { icon: <CheckCircle size={18} />, label: 'Pubblica!', color: 'bg-purple-100 text-purple-700' },
          ].map((item, idx) =>
            item.label ? (
              <div key={idx} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${item.color}`}>
                {item.icon}
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ) : (
              <span key={idx} className={item.color}>{item.icon}</span>
            ),
          )}
        </div>
      </div>

      {/* Sezioni guida */}
      <div className="space-y-3">
        {visible.map((section) => (
          <DocSection key={section.id} section={section} />
        ))}
      </div>

      {!showAll && (
        <button
          type="button"
          className="w-full py-3 rounded-xl border-2 border-dashed border-border text-text-secondary hover:border-primary hover:text-primary transition-colors text-sm font-medium"
          onClick={() => setShowAll(true)}
        >
          Mostra tutte le sezioni della guida ({sections.length - 4} ancora da vedere)
        </button>
      )}

      {/* Footer note */}
      <div className="rounded-xl bg-gray-50 border border-border p-5 flex gap-4">
        <Shield size={20} className="text-text-secondary shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-text-primary mb-1">Hai bisogno di supporto tecnico?</p>
          <p className="text-sm text-text-secondary">
            Per problemi tecnici non risolti con questa guida, contatta il team tecnico Glooci all'indirizzo{' '}
            <a href="mailto:supporto@glooci.it" className="text-primary underline hover:no-underline">supporto@glooci.it</a>{' '}
            o tramite il portale di gestione. Per segnalare errori nei contenuti culturali, rivolgiti al tuo responsabile di progetto.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Documentazione;
